import { EllipsisVertical, Plus, Search } from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '@waymail/ui';
import { useBuscasSalvas } from '../lib/consultas';
import { useUi } from '../store/ui';
import type { PedidoDeBusca } from './DialogoDeBuscaSalva';

const DialogoDeBuscaSalva = lazy(() =>
  import('./DialogoDeBuscaSalva').then((m) => ({ default: m.DialogoDeBuscaSalva })),
);

/**
 * "Buscas salvas" da barra lateral (ADR 0050). Aplicar abre a paleta ja com a
 * consulta: ela busca em todas as pastas e mostra o que e parcial. O + guarda a
 * busca que esta na caixa de busca da lista.
 */
export function BuscasSalvas({ accountId }: { accountId: string | null }) {
  const buscas = useBuscasSalvas();
  const consultaAtual = useUi((s) => s.query).trim();
  const abrirBuscaSalva = useUi((s) => s.abrirBuscaSalva);
  const [pedido, setPedido] = useState<PedidoDeBusca | null>(null);

  const itens = (buscas.data ?? []).filter(
    (b) => !b.smart && (b.accountId === null || b.accountId === accountId),
  );

  return (
    <section aria-label="Buscas salvas" className="mt-5">
      <div className="flex items-center justify-between pr-1">
        <p className="px-2.5 text-[12px] font-semibold text-tertiary">Buscas salvas</p>
        {consultaAtual !== '' && (
          <button
            type="button"
            aria-label="Salvar a busca atual"
            title="Salvar a busca atual"
            onClick={() => setPedido({ tipo: 'salvar', query: consultaAtual, accountId })}
            className="flex size-7 cursor-pointer items-center justify-center rounded-md text-tertiary hover:bg-hover hover:text-primary"
          >
            <Plus size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        )}
      </div>

      {buscas.isSuccess && itens.length === 0 && (
        <p className="px-2.5 pt-1 text-[12px] text-tertiary">
          Nenhuma ainda. Busque algo e salve pelo +.
        </p>
      )}

      {itens.length > 0 && (
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {itens.map((b) => (
            <li key={b.id} className="relative flex items-center">
              <button
                type="button"
                onClick={() => abrirBuscaSalva(b.query, b.accountId)}
                title={b.query}
                className="flex h-9 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 pr-9 text-left text-[13px] font-semibold text-primary hover:bg-hover"
              >
                <Search
                  size={16}
                  strokeWidth={1.5}
                  aria-hidden="true"
                  className="shrink-0 text-tertiary"
                />
                <span className="truncate">{b.name}</span>
              </button>
              <span className="absolute right-1">
                <Dropdown>
                  <DropdownTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Ações da busca ${b.name}`}
                      className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-tertiary hover:bg-hover hover:text-primary"
                    >
                      <EllipsisVertical size={16} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                  </DropdownTrigger>
                  <DropdownContent align="end">
                    <DropdownItem onSelect={() => setPedido({ tipo: 'renomear', busca: b })}>
                      Renomear
                    </DropdownItem>
                    <DropdownItem danger onSelect={() => setPedido({ tipo: 'excluir', busca: b })}>
                      Excluir
                    </DropdownItem>
                  </DropdownContent>
                </Dropdown>
              </span>
            </li>
          ))}
        </ul>
      )}

      {pedido !== null && (
        <Suspense fallback={null}>
          <DialogoDeBuscaSalva pedido={pedido} onFechar={() => setPedido(null)} />
        </Suspense>
      )}
    </section>
  );
}
